import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { api } from "./api";
import type { BootstrapData, PendingScope } from "./types";
import Dashboard from "./components/Dashboard";
import DealForm from "./components/DealForm";
import SearchPanel from "./components/SearchPanel";
import AdminPanel from "./components/AdminPanel";
import AuditHistory from "./components/AuditHistory";
import PendingTracker from "./components/PendingTracker";

type Tab = "dashboard" | "new" | "pending" | "search" | "audit" | "admin";

export default function App() {
  const {user, loading, signIn, signOut} = useAuth();
  const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null);
  const [tab, setTab] = useState<Tab>("dashboard");
  const [pendingScope, setPendingScope] = useState<PendingScope>("all_open");
  const [pendingAddMode, setPendingAddMode] = useState(false);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(false);

  async function loadBootstrap() {
    if (!user) return;
    setOpening(true);
    setError("");
    try {
      setBootstrap(await api.bootstrap());
    } catch (caught) {
      setBootstrap(null);
      setError(caught instanceof Error ? caught.message : "Desk Log access failed.");
    } finally {
      setOpening(false);
    }
  }

  useEffect(() => {
    setBootstrap(null);
    if (user) void loadBootstrap();
  }, [user]);

  const openPending = useCallback((scope: PendingScope) => {
    setPendingScope(scope);
    setPendingAddMode(false);
    setTab("pending");
  }, []);

  const addPending = useCallback(() => {
    setPendingScope("all_open");
    setPendingAddMode(true);
    setTab("pending");
  }, []);

  if (loading) {
    return <div className="center-screen"><div className="loader" /><p>Checking Google sign-in…</p></div>;
  }

  if (!user) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="brand-mark">AA</div>
          <span className="eyebrow">All American Ford &amp; Subaru</span>
          <h1>Desk Log v2</h1>
          <p>Fast, secure deal management powered by PostgreSQL. No annual spreadsheet conversion.</p>
          <button className="google-button" onClick={() => void signIn()}>Sign in with Google</button>
        </div>
      </div>
    );
  }

  if (opening) {
    return <div className="center-screen"><div className="loader" /><p>Opening Desk Log v2…</p></div>;
  }

  if (!bootstrap) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Access unavailable</h1>
          <p className="error">{error || "This Gmail account has not been authorized."}</p>
          <button className="secondary" onClick={() => void signOut()}>Sign out</button>
        </div>
      </div>
    );
  }

  const canManage = bootstrap.permissions.canManageDeals;
  const canManagePending = bootstrap.permissions.canManagePending;
  const canSignOffPending = bootstrap.permissions.canSignOffPending;
  const canAdmin = bootstrap.permissions.canAdminister;

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark small">AA</div>
          <div><strong>Desk Log v2</strong><span>{bootstrap.dealershipName}</span></div>
        </div>
        <div className="user-chip">
          <div><strong>{bootstrap.actor.name}</strong><span>{bootstrap.actor.role.replace("_", " ")}</span></div>
          <button className="link-button" onClick={() => void signOut()}>Sign out</button>
        </div>
      </header>

      <nav className="navigation">
        <button className={tab === "dashboard" ? "active" : ""} onClick={() => setTab("dashboard")}>Today &amp; MTD</button>
        {canManage && <button className={tab === "new" ? "active" : ""} onClick={() => setTab("new")}>Enter New Deal</button>}
        <button className={tab === "pending" ? "active" : ""} onClick={() => openPending("all_open")}>Pending Deals</button>
        <button className={tab === "search" ? "active" : ""} onClick={() => setTab("search")}>Search Deal History</button>
        {canAdmin && <button className={tab === "audit" ? "active" : ""} onClick={() => setTab("audit")}>Audit History</button>}
        {canAdmin && <button className={tab === "admin" ? "active" : ""} onClick={() => setTab("admin")}>Administration</button>}
      </nav>

      <main>
        {tab === "dashboard" && <Dashboard options={bootstrap.options} canManagePending={canManagePending} onOpenPending={openPending} onAddPending={addPending} />}
        {tab === "new" && canManage && (
          <DealForm options={bootstrap.options} onSaved={() => setTab("dashboard")} />
        )}
        {tab === "pending" && (
          <PendingTracker
            options={bootstrap.options}
            canManage={canManagePending}
            canSignOff={canSignOffPending}
            initialScope={pendingScope}
            startInAddMode={pendingAddMode}
            onAddModeOpened={() => setPendingAddMode(false)}
          />
        )}
        {tab === "search" && (
          <SearchPanel
            options={bootstrap.options}
            canManage={canManage}
            canViewRemoved={bootstrap.permissions.canViewRemoved}
            canRestore={canAdmin}
          />
        )}
        {tab === "audit" && canAdmin && <AuditHistory />}
        {tab === "admin" && canAdmin && <AdminPanel />}
      </main>
    </div>
  );
}
